import { availableLanguages, weenatIntl } from '@weenat/wintl';
import isDate from 'lodash-es/isDate';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import set from 'lodash-es/set';
import * as s from 'superstruct';
import { BillingStatus } from './enums';
import { measurementReportWateringTypeValues } from './resources/measurementsReports.types';
import { GREEN_BEAN_AUTHORIZED_DEPARTMENTS, arvalisCropTypes, isGreenBean, isSeedCorn } from './resources/waterBalanceArvalis.type';
export var ValidatorType;
(function (ValidatorType) {
    ValidatorType["literal"] = "literal";
    ValidatorType["union"] = "union";
    ValidatorType["sizedString"] = "sizedString";
    ValidatorType["email"] = "email";
    ValidatorType["nonEmptyString"] = "nonEmptyString";
    ValidatorType["nonEmptyArray"] = "nonEmptyArray";
    ValidatorType["isTrue"] = "isTrue";
    ValidatorType["isOptionalOrNot"] = "isOptionalOrNot";
    ValidatorType["number"] = "number";
    ValidatorType["optional"] = "optional";
    ValidatorType["optionalNumber"] = "optionalNumber";
    ValidatorType["optionalDate"] = "optionalDate";
    ValidatorType["string"] = "string";
    ValidatorType["optionalString"] = "optionalString";
    ValidatorType["boolean"] = "boolean";
    ValidatorType["optionalBoolean"] = "optionalBoolean";
    ValidatorType["date"] = "date";
    ValidatorType["enums"] = "enums";
    ValidatorType["epicureUsername"] = "epicureUsername";
    ValidatorType["dynamic"] = "dynamic";
    ValidatorType["array"] = "array";
    ValidatorType["time"] = "time";
    ValidatorType["phoneNumber"] = "phoneNumber";
    ValidatorType["object"] = "object";
    ValidatorType["optionalObject"] = "optionalObject";
    ValidatorType["soilTextureComposition"] = "soilTextureComposition";
})(ValidatorType || (ValidatorType = {}));
const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const emailDomainAccentRegex = /([à-ü]|[À-Ü])/;
// Regex checking for HH:MM with leading zeros
const timeRegex = /^([0-2][0-3]|[0-1][0-9]):[0-5][0-9]+$/;
/**
 * Check if param is number or string representing a number
 */
const isNumber = (x) => {
    return typeof x === 'number' || (typeof x === 'string' && !isNaN(parseInt(x, 10)));
};
/**
 * Validator if checked boolean is true
 */
const isOptionalOrNot = ({ checkedName, validation }) => {
    return s.dynamic((val, ctx) => {
        const formValues = ctx.branch[0];
        const checked = formValues[checkedName];
        if (!checked)
            return s.define(ValidatorType.boolean, () => true);
        return s.define(ValidatorType.isOptionalOrNot, (value) => {
            const [error] = validation.validate(value);
            return error ? error.message : true;
        });
    });
};
const isBetween = ({ min, max, strict = true, errorMessageKey, allowNil = false }) => {
    return s.dynamic((val, ctx) => {
        return s.define(ValidatorType.number, (x) => {
            const formValues = ctx.branch[0];
            const minimum = typeof min === 'number' ? min : parseInt(formValues[min], 10);
            const maximum = typeof max === 'number' ? max : parseInt(formValues[max], 10);
            let result = true;
            if (!isNil(x) && isNumber(x)) {
                const value = parseInt(x, 10);
                if (strict) {
                    if (!(value > minimum && value < maximum)) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        result = weenatIntl.t(errorMessageKey);
                    }
                }
                else {
                    if (!(value >= minimum && value <= maximum)) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        result = weenatIntl.t(errorMessageKey);
                    }
                }
            }
            else if ((!isNil(x) && !isNumber(x)) || (isNil(x) && !allowNil)) {
                result = weenatIntl.t('forms.field_is_invalid');
            }
            return result;
        });
    });
};
const nonEmptyString = s.define(ValidatorType.nonEmptyString, (value) => (!isEmpty(value) && value.trim().length > 0) || weenatIntl.t('forms.field_is_empty'));
const percentage = () => isBetween({
    min: 0,
    max: 100,
    strict: false,
    errorMessageKey: 'forms.percentage_error'
});
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function nonEmptyArray(struct) {
    return s.refine(!isNil(struct) ? s.array(struct) : s.array(), ValidatorType.nonEmptyArray, (value) => !isEmpty(value) || weenatIntl.t('forms.field_is_empty'));
}
function sizedString({ minChars, maxChars, required = false }) {
    return s.define(ValidatorType.sizedString, (value) => {
        const isNilOrEmpty = isNil(value) || isEmpty(value);
        return !required && isNilOrEmpty
            ? true
            : required && isNilOrEmpty
                ? weenatIntl.t('forms.field_is_empty')
                : value.length < minChars
                    ? weenatIntl.t('forms.field_size_too_low', { minChars })
                    : value.length > maxChars
                        ? weenatIntl.t('forms.field_size_too_high', { maxChars })
                        : true;
    });
}
export const validators = {
    addressLine1: (required = false) => sizedString({ minChars: required ? 1 : 0, maxChars: 128, required }),
    addressLine2: sizedString({ minChars: 0, maxChars: 128, required: false }),
    siret: (required = false) => sizedString({ minChars: 14, maxChars: 14, required }),
    city: (required = false) => sizedString({ minChars: 0, maxChars: 128, required }),
    zipCode: (required = false) => sizedString({ minChars: 0, maxChars: 32, required }),
    sizedString,
    union: s.union,
    literal: s.literal,
    array: s.array,
    enum: s.enums,
    optional: s.optional,
    phoneNumber: s.object({
        telephoneCountryCode: nonEmptyString,
        enabled: s.boolean(),
        telephoneNumber: nonEmptyString
    }),
    epicureUsername: s.refine(nonEmptyString, ValidatorType.epicureUsername, (x) => (x.match(/^[a-zA-Z0-9]+$/) && x.length >= 4) ||
        weenatIntl.t('dss.decitrait.errors.eipureUsername_field_invalid')),
    email: s.define(ValidatorType.email, (x) => {
        if (isEmpty(x) || x.trim().length == 0)
            return weenatIntl.t('forms.field_is_empty');
        const domain = x.split('@')[1] || '';
        if (emailDomainAccentRegex.test(domain)) {
            return weenatIntl.t('forms.field_is_invalid_with_special_char');
        }
        else {
            return (emailRegex.test(x.toLowerCase().trim()) ||
                weenatIntl.t('forms.field_is_invalid'));
        }
    }),
    time: s.define(ValidatorType.time, (x) => {
        return (timeRegex.test(x.toLowerCase().trim()) ||
            weenatIntl.t('forms.invalid_time_field_input'));
    }),
    optionalNumber: s.define(ValidatorType.optionalNumber, (x) => {
        return isNumber(x) || isEmpty(x) || weenatIntl.t('forms.field_is_invalid');
    }),
    nonEmptyString,
    boolean: s.boolean(),
    optionalBoolean: s.optional(s.boolean()),
    // TODO: refine message
    number: s.define(ValidatorType.number, (x) => {
        return isNumber(x) || weenatIntl.t('forms.field_is_invalid');
    }),
    date: s.define(ValidatorType.date, (x) => {
        return isNil(x)
            ? weenatIntl.t('forms.field_is_empty')
            : isDate(x) || weenatIntl.t('forms.field_is_invalid');
    }),
    optionalDate: s.define(ValidatorType.optionalDate, (x) => {
        return isDate(x) || isNil(x) || weenatIntl.t('forms.field_is_invalid');
    }),
    string: s.string(),
    billingStatus: s.enums([BillingStatus.essential, BillingStatus.expert, BillingStatus.plus]),
    optionalString: s.optional(s.string()),
    membershipCode: sizedString({ minChars: 0, maxChars: 30, required: false }),
    isTrue: s.define(ValidatorType.isTrue, (x) => x === true || weenatIntl.t('forms.field_is_empty')),
    wateringType: s.enums(measurementReportWateringTypeValues),
    between: isBetween,
    isOptionalOrNot: isOptionalOrNot,
    percentage,
    language: s.enums(availableLanguages),
    nonEmptyArray,
    RFU: s.optional(s.dynamic((val, ctx) => {
        return s.define(ValidatorType.number, (x) => {
            const formValues = ctx.branch[0];
            const ruFieldName = 'ruMax';
            const ruValue = parseInt(formValues[ruFieldName], 10);
            const min = Math.floor(ruValue * 0.45);
            const max = Math.floor(ruValue * 0.75);
            const { valuesFromArvalis } = formValues;
            if (valuesFromArvalis) {
                return true;
            }
            let result = true;
            if (!isNil(x) && isNumber(x)) {
                const value = parseInt(x, 10);
                if (!(value > min && value < max)) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    result = weenatIntl.t('dss.waterBalance.available_water_capacities_rules.specific_range_with_value', { max, min });
                }
            }
            else if (!isNil(x) && !isNumber(x)) {
                result = weenatIntl.t('forms.field_is_invalid');
            }
            return result;
        });
    })),
    object: s.object,
    optionalObject: (obj) => s.optional(s.object(obj)),
    variety: s.dynamic((val, ctx) => {
        return s.define(ValidatorType.string, (x) => {
            const formValues = ctx.branch[0];
            const cropType = formValues['cropType'];
            if (!isNil(cropType)) {
                if (isSeedCorn(cropType)) {
                    return isEmpty(x) || weenatIntl.t('forms.field_is_invalid');
                }
                else if (isGreenBean(cropType)) {
                    return true;
                }
                else {
                    return !isEmpty(x) || weenatIntl.t('forms.field_is_empty');
                }
            }
            else {
                return true;
            }
        });
    }),
    cropType: s.dynamic((val, ctx) => {
        return s.define(ValidatorType.string, (cropType) => {
            if (isGreenBean(cropType)) {
                const formValues = ctx.branch[0];
                const departmentCode = parseInt(formValues['departmentCode'], 10);
                return (GREEN_BEAN_AUTHORIZED_DEPARTMENTS.includes(departmentCode) ||
                    weenatIntl.t('dss.waterBalance.arvalis_plot_creation.green_bean_authorized_department', {
                        cropType: weenatIntl.t(`dss.waterBalance.arvalis_crop_types_names.${cropType}`),
                        departmentCode,
                        departmentList: GREEN_BEAN_AUTHORIZED_DEPARTMENTS.join(', ')
                    }));
            }
            else {
                return (arvalisCropTypes.includes(cropType) ||
                    weenatIntl.t('forms.field_is_invalid'));
            }
        });
    }),
    soilTextureComposition: s.refine(s.object({
        silt: percentage(),
        sand: percentage(),
        clay: percentage()
    }), ValidatorType.soilTextureComposition, (value) => {
        if (!isNil(value)) {
            const { silt, sand, clay } = value;
            if (!isNil(silt) && !isNil(sand) && !isNil(clay) && silt + sand + clay === 100) {
                return true;
            }
            else {
                return weenatIntl.t('models.plot.soil_composition_errors.not_equal_to_100');
            }
        }
        else {
            return weenatIntl.t('forms.fill_all_fields_error');
        }
    })
};
const unhandledValidatorInitialValue = (validatorType) => {
    throw new Error(`No validator initial value is defined for the validator: ${validatorType}`);
};
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function superscheme(definition) {
    const struct = s.object(definition);
    const initialValues = Object.entries(definition).reduce((acc, [key, { type }]) => {
        const now = new Date(Date.now());
        const t = type;
        // @ts-expect-error key isn't typed, so ts considers it a string
        acc[key] =
            t === ValidatorType.literal ||
                t === ValidatorType.union ||
                t === ValidatorType.date ||
                t === ValidatorType.optionalDate ||
                t == ValidatorType.optionalBoolean ||
                t == ValidatorType.optional
                ? undefined
                : t === ValidatorType.time
                    ? `${now.getHours()}:${now.getMinutes()}`
                    : t === ValidatorType.enums
                        ? undefined
                        : t === ValidatorType.string ||
                            t === ValidatorType.optionalString ||
                            t === ValidatorType.nonEmptyString ||
                            t === ValidatorType.sizedString ||
                            t === ValidatorType.email ||
                            t === ValidatorType.number ||
                            t === ValidatorType.optionalNumber ||
                            t === ValidatorType.dynamic
                            ? undefined
                            : t === ValidatorType.array || t === ValidatorType.nonEmptyArray
                                ? []
                                : t === ValidatorType.boolean || t === ValidatorType.isTrue
                                    ? false
                                    : t === ValidatorType.object
                                        ? {}
                                        : t === ValidatorType.phoneNumber
                                            ? { telephoneCountryCode: 'FR', enabled: false, telephoneNumber: '' }
                                            : unhandledValidatorInitialValue(t);
        return acc;
    }, {});
    const validate = createStructValidator(struct);
    return {
        initialValues,
        struct,
        validate
    };
}
function createStructValidator(struct) {
    return (values) => {
        try {
            s.assert(values, struct);
        }
        catch (error) {
            if (error instanceof s.StructError) {
                return error.failures().reduce((acc, { path, message, type }) => {
                    const errorMsg = type === 'enums' ? weenatIntl.t('forms.field_is_invalid') : message;
                    set(acc, path, errorMsg);
                    return acc;
                }, {});
            }
        }
        return {};
    };
}
export default superscheme;
